import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Bot, Sparkles, FlaskConical, Pill, Brain, Heart, Search, AlertCircle } from "lucide-react";

const quickPrompts = [
  { text: "Tell me about Meropenem", icon: FlaskConical },
  { text: "What is Caspofungin used for?", icon: Pill },
  { text: "Compare DRUNEM vs DRUNEM-XP", icon: Search },
  { text: "India pharma industry trends", icon: Brain },
  { text: "Antifungal drugs by Drukst", icon: Heart },
];

export default function AiChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Focus input when chat opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;

    setInput("");
    setShowWelcome(false);
    setError(null);
    setMessages(prev => [...prev, { role: "user", text: msg }]);
    setLoading(true);

    try {
      // Determine API URL - works for both development and production
      const apiUrl = window.location.hostname === 'localhost' || window.location.hostname.includes('preview.emergentagent')
        ? '/api/chat.php'  // Development - proxied
        : '/api/chat.php'; // Production - same domain
      
      console.log('[DrukstAI] Sending request to:', apiUrl);
      console.log('[DrukstAI] Message:', msg.substring(0, 50) + '...');

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          message: msg,
          session_id: sessionId,
        }),
      });

      console.log('[DrukstAI] Response status:', response.status);

      // Get response text first for debugging
      const responseText = await response.text();
      console.log('[DrukstAI] Response text (first 200 chars):', responseText.substring(0, 200));

      // Try to parse as JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('[DrukstAI] JSON parse error:', parseError);
        console.error('[DrukstAI] Raw response:', responseText.substring(0, 500));
        throw new Error('Invalid response from server');
      }

      console.log('[DrukstAI] Parsed response:', data);

      // Check for error in response
      if (data.error) {
        throw new Error(data.error);
      }

      // Update session and add AI message
      if (data.session_id) {
        setSessionId(data.session_id);
      }

      if (data.response) {
        setMessages(prev => [...prev, { role: "ai", text: data.response }]);
      } else {
        throw new Error('No response content received');
      }

    } catch (err) {
      console.error('[DrukstAI] Error:', err);
      
      let errorMessage = "I'm having trouble connecting right now.";
      
      if (err.message.includes('fetch') || err.message.includes('network')) {
        errorMessage = "Network error. Please check your connection and try again.";
      } else if (err.message.includes('Invalid response')) {
        errorMessage = "Server returned an unexpected response. Please try again.";
      } else if (err.message.includes('budget') || err.message.includes('Budget')) {
        errorMessage = "I'm temporarily unavailable due to high demand.";
      }

      setMessages(prev => [...prev, { 
        role: "ai", 
        text: `${errorMessage}\n\nPlease contact us at **info@drukstbiotech.com** or call **+91 9250019785** for assistance.`
      }]);
      
      setError(err.message);
    }
    
    setLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  const formatText = (text) => {
    if (!text) return null;
    
    return text.split('\n').map((line, i) => {
      // Parse bold text
      const boldParsed = line.split(/\*\*(.*?)\*\*/g).map((part, j) =>
        j % 2 === 1 ? <strong key={j} className="text-white font-semibold">{part}</strong> : part
      );
      
      // List items
      if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
        return <li key={i} className="ml-3 list-disc text-[#C8D6E5] text-[12px] leading-relaxed">{boldParsed}</li>;
      }
      
      // Headers
      if (line.trim().startsWith('###')) {
        return <p key={i} className="font-bold text-[#2DD4BF] text-[13px] mt-3 mb-1">{line.replace(/^#+\s*/, '')}</p>;
      }
      if (line.trim().startsWith('#')) {
        return <p key={i} className="font-bold text-white text-[13px] mt-2 mb-1">{line.replace(/^#+\s*/, '')}</p>;
      }
      
      // Empty lines
      if (line.trim() === '') return <br key={i} />;
      
      // Regular paragraphs
      return <p key={i} className="mb-1 text-[12.5px] leading-relaxed">{boldParsed}</p>;
    });
  };

  const clearChat = () => {
    setMessages([]);
    setShowWelcome(true);
    setError(null);
    setSessionId(null);
  };

  return (
    <>
      {/* Floating button */}
      <motion.button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen(!open)}
        className="fixed bottom-24 md:bottom-8 right-6 z-50 group"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <div className="relative w-14 h-14 rounded-full bg-[#2DD4BF] text-[#022C22] flex items-center justify-center shadow-lg shadow-[#2DD4BF]/25">
          {open ? <X size={22} /> : <Sparkles size={22} />}
          {!open && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-white flex items-center justify-center">
              <span className="text-[8px] font-bold text-[#022C22]">AI</span>
            </span>
          )}
        </div>
        {!open && (
          <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 rounded-lg bg-[#0F172A] border border-white/10 text-[11px] text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            Ask DrukstAI about any drug
          </div>
        )}
      </motion.button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-chat-panel"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-40 md:bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] h-[520px] max-h-[calc(100vh-200px)] rounded-2xl overflow-hidden border border-white/10 bg-[#0B1120] shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-white/5 bg-gradient-to-r from-[#0B1120] to-[#0F172A] flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2DD4BF] to-[#3B82F6] flex items-center justify-center">
                <Bot size={20} className="text-white" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-bold text-white">DrukstAI</h4>
                <p className="text-[10px] text-[#2DD4BF]">Pharmaceutical Knowledge Engine</p>
              </div>
              <div className="flex items-center gap-2">
                {messages.length > 0 && (
                  <button
                    onClick={clearChat}
                    className="text-[9px] text-[#94A3B8] hover:text-white px-2 py-1 rounded hover:bg-white/10 transition-colors"
                  >
                    Clear
                  </button>
                )}
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-500/10">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-[9px] text-green-400 font-medium">Live</span>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {/* Error banner */}
              {error && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-[11px]">
                  <AlertCircle size={14} />
                  <span>Connection issue. Check console for details.</span>
                </div>
              )}

              {/* Welcome screen */}
              {showWelcome && messages.length === 0 && (
                <div className="text-center py-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#2DD4BF]/20 to-[#3B82F6]/20 flex items-center justify-center mx-auto mb-4">
                    <Sparkles size={28} className="text-[#2DD4BF]" />
                  </div>
                  <h3 className="text-base font-bold text-white mb-1">Welcome to DrukstAI</h3>
                  <p className="text-[11px] text-[#94A3B8] mb-5 leading-relaxed px-4">
                    Your pharmaceutical knowledge companion. Ask about any drug salt, mechanism of action, or explore Drukst Biotech products.
                  </p>
                  <div className="space-y-2">
                    {quickPrompts.map((prompt, i) => (
                      <button
                        key={i}
                        data-testid={`quick-prompt-${i}`}
                        onClick={() => sendMessage(prompt.text)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-[#2DD4BF]/30 hover:bg-white/[0.05] text-left transition-all group"
                      >
                        <prompt.icon size={14} className="text-[#2DD4BF]/60 group-hover:text-[#2DD4BF] shrink-0" />
                        <span className="text-[12px] text-[#94A3B8] group-hover:text-white transition-colors">{prompt.text}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Messages */}
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[88%] rounded-2xl px-4 py-3 ${
                      msg.role === "user"
                        ? "bg-[#2DD4BF] text-[#022C22] rounded-br-md text-[13px]"
                        : "bg-white/[0.04] text-[#C8D6E5] rounded-bl-md border border-white/5"
                    }`}
                  >
                    {msg.role === "ai" ? formatText(msg.text) : <span className="text-[13px]">{msg.text}</span>}
                  </div>
                </div>
              ))}

              {/* Loading indicator */}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white/[0.04] rounded-2xl rounded-bl-md px-5 py-4 border border-white/5">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 rounded-full bg-[#2DD4BF] animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-2 h-2 rounded-full bg-[#2DD4BF] animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-2 h-2 rounded-full bg-[#2DD4BF] animate-bounce" style={{ animationDelay: "300ms" }} />
                      </div>
                      <span className="text-[10px] text-[#94A3B8] ml-1">Researching...</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <form onSubmit={handleSubmit} className="px-4 py-3 border-t border-white/5 bg-[#0B1120]">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  data-testid="ai-chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about any salt, drug, or product..."
                  className="flex-1 h-11 px-4 rounded-xl bg-white/5 border border-white/10 text-white text-[13px] placeholder:text-white/25 focus:outline-none focus:border-[#2DD4BF]/50 transition-colors"
                  disabled={loading}
                />
                <button
                  type="submit"
                  data-testid="ai-chat-send"
                  disabled={loading || !input.trim()}
                  className="w-11 h-11 rounded-xl bg-[#2DD4BF] text-[#022C22] flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-30 disabled:hover:scale-100 shrink-0"
                >
                  <Send size={16} />
                </button>
              </div>
              <p className="text-[9px] text-[#94A3B8]/40 text-center mt-2">
                For healthcare professionals. Always consult prescribing information.
              </p>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
